import { useMemo, useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useImmer } from 'use-immer'
import { Box, Button, Flex, FormControl, FormLabel, IconButton, Input, Select, Text, Textarea, VStack } from '@chakra-ui/react'
import { ARTWORK_REQUEST_PRIORITIES, ROLES } from 'lib'
import { MdAttachment } from 'react-icons/md'
import { FILE_ATTACHMENT_STATUS, FileAttachment, UiSelect } from 'components/common'
import { deleteArtworkRequestNoteAttachmentByPath, getUsersByRoles, uploadArtworkRequestNoteAttachment } from 'api'

export const ArtworkRequestFormType = {
  Add: 'add',
  Edit: 'edit'
}

export const ArtworkRequestForm = ({ type, onSubmit, isSubmitting, initialForm = null }) => {
  const [form, setForm] = useImmer({
    quoteNumber: '',
    projectName: '',
    assignedGraphicDesigner: null,
    priority: '',
    notes: '',
    attachments: []
  })
  const [nextAttachmentId, setNextAttachmentId] = useState(1)
  const fileInputRef = useRef(null)

  const buttonText = useMemo(() => {
    switch (type) {
      case ArtworkRequestFormType.Add:
        return 'Add Request'
      case ArtworkRequestFormType.Edit:
        return 'Save Request'
    }
  }, [type])

  useEffect(() => {
    setForm(draft => {
      Object.keys(initialForm || {}).forEach(k => { draft[k] = initialForm[k] ?? '' })
    })
  }, [initialForm])

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(form)
  }

  const handleFileChange = async (event) => {
    if (event.target.files && event.target.files.length) {
      const files = event.target.files
      // eslint-disable-next-line space-unary-ops
      for (let i = 0; i < files.length; i ++) {
        const file = files[i]
        const curAttachmentId = nextAttachmentId + i
        const attachment = {
          id: curAttachmentId,
          status: 'uploading',
          progress: 0
        }
        setForm(draft => { draft.attachments = [...draft.attachments, attachment] })
        try {
          const response = await uploadArtworkRequestNoteAttachment({ file }, progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setForm(draft => {
              draft.attachments = draft.attachments.map(v => v.id === curAttachmentId ? { ...v, progress: percentCompleted } : v)
            })
          })
          setForm(draft => {
            draft.attachments = draft.attachments.map(v => v.id === curAttachmentId ? { ...response, status: 'success' } : v)
          })
        } catch (err) {
          setForm(draft => {
            draft.attachments = draft.attachments.map(v => v.id === curAttachmentId ? { ...v, status: 'fail' } : v)
          })
        }
      }
      setNextAttachmentId(nextAttachmentId + files.length)
    }
  }

  const onAddAttachment = () => {
    fileInputRef.current.click()
  }

  return (
    <form onSubmit={handleSubmit}>
      <VStack gap={2} maxW="500px">
        <FormControl>
          <FormLabel>Quote No (optional)</FormLabel>
          <Input name="quoteNumber" value={form.quoteNumber} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Project Name</FormLabel>
          <Input name="projectName" value={form.projectName} onChange={handleChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Assigned Graphic Designer</FormLabel>
          <UiSelect
            value={form.assignedGraphicDesigner}
            onChange={v => {
              setForm(draft => {
                draft.assignedGraphicDesigner = { ...v }
              })
            }}
            loadData={(page, search) => getUsersByRoles(page, 20, search, 'name', 'asc', [ROLES.GraphicDesigner, ROLES.SeniorGraphicDesigner], true)}
            apiRespProp="rows"
            getOptionValue={v => v.id}
            getOptionLabel={v => v.name}
            placeholder="Search Users"
            noOptionsMessage={() => 'No Users Found'}
            required={true}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Priority</FormLabel>
          <Select name="priority" value={form.status} onChange={handleChange}>
            <option value=""></option>
            {Object.values(ARTWORK_REQUEST_PRIORITIES).map(v => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Notes</FormLabel>
          <Textarea name="notes" rows={6} onChange={handleChange} value={form.notes} />
        </FormControl>
      </VStack>
      <Box my={8}>
        {form.attachments.length !== 0 && (
          <Flex gap={4} flexWrap="wrap" my={4}>
            {form.attachments.map(attachment => {
              return (
                <FileAttachment
                  key={attachment.status === FILE_ATTACHMENT_STATUS.Success ? attachment.path : attachment.id}
                  canRemove={true}
                  id={attachment.id}
                  status={attachment.status}
                  progress={attachment.progress}
                  filename={attachment.filename || ''}
                  extension={attachment.extension || ''}
                  path={attachment.path || ''}
                  onRemove={async () => {
                    if (attachment.status === FILE_ATTACHMENT_STATUS.Success) {
                      try {
                        await deleteArtworkRequestNoteAttachmentByPath({ path: attachment.path })
                      } catch (err) {
                        console.log('Failed to remove temp file on the server.')
                      }
                    }
                    setForm(draft => {
                      draft.attachments = draft.attachments.filter(v => {
                        return attachment.status === FILE_ATTACHMENT_STATUS.Success ? (v.path !== attachment.path) : (v.id !== attachment.id)
                      })
                    })
                  }}
                />
              )
            })}
          </Flex>
        )}
        <Flex gap={2} cursor="pointer" alignItems="center" onClick={onAddAttachment} mb={8}>
          <IconButton
            variant="ghost"
            size="xs"
            colorScheme="gray"
            icon={<MdAttachment fontSize="18px" />}
          />
          <Text fontSize="xs">Add Attachment</Text>
          <Input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} multiple />
        </Flex>
        <Button type="submit" size="md" colorScheme="blue" isLoading={isSubmitting}>{buttonText}</Button>
      </Box>
    </form>
  )
}

ArtworkRequestForm.propTypes = {
  type: PropTypes.oneOf(Object.values(ArtworkRequestFormType)).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  initialForm: PropTypes.object
}
